import React, { useState, useEffect } from "react";
import { Select, message, Button } from "antd";
import { putRequest, getRequest } from "../service/index";
import { getEmployeeData } from "../utils/localStorage";

const yesNoOptions = [
  { label: "eligible", value: 'eligible' },
  { label: "noteligible", value: 'noteligible' }
];

// Dynamic option lists (fetched from APIs)
const useOptions = () => {
  const [employees, setEmployees] = useState([]);
  const [reportTo, setReportTo] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    const pick = (obj, keys) => keys.find((k) => obj?.[k] !== undefined);
    const mapGeneric = (arr = [], labelKeys = ["name", "title", "label"], valueKeys = ["_id", "id", "value"]) => (
      (Array.isArray(arr) ? arr : []).map((item) => {
        const valueKey = pick(item, valueKeys) || valueKeys[0];
        const labelKey = pick(item, labelKeys) || labelKeys[0];
        const label = item?.label ?? item?.[labelKey] ?? String(item?.[valueKey] ?? "");
        const value = item?.[valueKey] ?? undefined;
        return { label, value, raw: item };
      })
    );
    const mapEmployees = (arr = [], designationIdToName = new Map()) => (
      (Array.isArray(arr) ? arr : []).map((emp) => {
        const id = emp?._id ?? emp?.id ?? emp?.employeeId ?? emp?.userId;
        const fullName = [emp?.firstName, emp?.lastName].filter(Boolean).join(" ") || emp?.fullName || emp?.name || String(id || "");
        const empDesignationName = (
          typeof emp?.designation === "string" ? designationIdToName.get(emp.designation) :
          (emp?.designation?.designationName || emp?.designation?.name || emp?.designation?.title)
        );
        const label = empDesignationName ? `${fullName} (${empDesignationName})` : fullName;
        return { label, value: id, raw: emp };
      })
    );

    const fetchAll = async () => {
      try {
        const [empRes, rptRes, locRes, depRes, leavesRes, shiftsRes, desigRes] = await Promise.all([
          getRequest("/api/employees"),
          getRequest("/api/companies/reportto"),
          getRequest("/api/companies/locations"),
          getRequest("/api/department"),
          getRequest("/api/leaves"),
          getRequest("/api/companies/shifts"),
          getRequest("/api/companies/designation"),
        ]);

        // Designations: build map id -> designationName and set options with designationName as label
        const designationsArr = desigRes?.designations ?? desigRes?.data ?? desigRes ?? [];
        const designationIdToName = new Map(
          (Array.isArray(designationsArr) ? designationsArr : []).map(d => [
            d?._id ?? d?.id,
            d?.designationName ?? d?.name ?? d?.title
          ])
        );
        setDesignations((Array.isArray(designationsArr) ? designationsArr : []).map(d => ({
          label: d?.designationName ?? d?.name ?? d?.title,
          value: d?._id ?? d?.id,
          raw: d
        })));

        // Employees and Reporting To labels include designation name
        const employeesArr = empRes?.employees ?? empRes ?? [];
        setEmployees(mapEmployees(employeesArr, designationIdToName));

        const reportToArr = rptRes?.employees ?? rptRes?.data ?? rptRes ?? [];
        setReportTo(mapEmployees(reportToArr, designationIdToName));

        setLocations(mapGeneric(locRes?.locations ?? locRes ?? [], ["name", "locationName", "title"], ["_id", "id"]));

        const departmentsArr = depRes?.departments ?? depRes ?? [];
        setDepartments(mapGeneric(departmentsArr, ["name", "title", "department"], ["_id", "id"]));

        const collectedSubDeps = [];
        (Array.isArray(departmentsArr) ? departmentsArr : []).forEach((d) => {
          if (Array.isArray(d?.subDepartments)) {
            collectedSubDeps.push(...d.subDepartments);
          }
        });
        setSubDepartments(mapGeneric(collectedSubDeps, ["name", "title"], ["_id", "id"]));

        setLeavePolicies(mapGeneric(leavesRes?.leaveConfigs ?? leavesRes?.leaves ?? leavesRes ?? [], ["title", "name"], ["_id", "id"]));

        setShifts(mapGeneric(shiftsRes?.shifts ?? shiftsRes ?? [], ["shiftName", "name", "title"], ["_id", "id"]));
      } catch (err) {
        console.error("Failed to load options:", err);
        message.error("Failed to load select options");
      }
    };

    fetchAll();
  }, []);

  return { employees, reportTo, locations, departments, subDepartments, designations, leavePolicies, shifts };
};

const BulkEditEmployee = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    employees: [],
    workLocation: undefined,
    designation: undefined,
    department: undefined,
    subDepartment: undefined,
    reportingTo: undefined,
    medicalInsurance: undefined,
    travel: undefined,
    bonus: undefined,
    leavePolicy: [],
    attendancePolicy: undefined,
    shift: undefined
  });
  const { employees, reportTo, locations, departments, subDepartments, designations, leavePolicies, shifts } = useOptions();

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build payload per API contract
      const user = getEmployeeData() || {};
      const companyId = user?.companyId || user?.company?.id || user?.company?._id || undefined;

      const selectedEmployeeIds = Array.isArray(formData.employees) ? formData.employees : [];

      const attendanceWeb = formData.attendancePolicy === "attendance_web" ? true : undefined;
      const attendanceMachine = formData.attendancePolicy === "attendance_machine" ? true : undefined;

      const employeePayload = selectedEmployeeIds.map((empId) => {
        const p = { _id: empId };
        if (formData.shift) p.shift = formData.shift;
        if (formData.workLocation) p.location = formData.workLocation;
        if (formData.designation) p.designation = formData.designation;
        if (formData.department) p.department = formData.department;
        if (formData.subDepartment) p.sub_department = formData.subDepartment;
        if (formData.reportingTo) p.reporting_to = formData.reportingTo;
        if (Array.isArray(formData.leavePolicy) && formData.leavePolicy.length > 0) p.annual_leave_policy = formData.leavePolicy;
        if (attendanceWeb !== undefined) p.attendanceWeb = attendanceWeb;
        if (attendanceMachine !== undefined) p.attendanceMachine = attendanceMachine;
        if (formData.medicalInsurance) p.medical = formData.medicalInsurance;
        if (formData.travel) p.travel = formData.travel;
        if (formData.bonus) p.bouns = formData.bonus;
        return p;
      });

      const payload = {
        ...(companyId ? { companyId } : {}),
        employees: employeePayload,
      };

      const response = await putRequest("/api/employees/bulk", payload);
      if (!response?.error) {
        message.success("Changes applied successfully");
        onClose && onClose();
      } else {
        message.error(response.error || "Failed to apply changes");
      }
    } catch (error) {
      message.error("Failed to apply changes. Please try again.");
      console.error("Bulk edit error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 flex flex-col gap-1">
          <label className="text-md">Select Employees</label>
          <Select
            mode="multiple"
            allowClear
            placeholder="Select employees"
            options={employees}
            value={formData.employees}
            onChange={(v) => updateField("employees", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Work Location</label>
          <Select
            allowClear
            placeholder="Select work location"
            options={locations}
            value={formData.workLocation}
            onChange={(v) => updateField("workLocation", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Designation</label>
          <Select
            allowClear
            placeholder="Select designation"
            options={designations}
            value={formData.designation}
            onChange={(v) => updateField("designation", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Department</label>
          <Select
            allowClear
            placeholder="Select department"
            options={departments}
            value={formData.department}
            onChange={(v) => updateField("department", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Sub Department</label>
          <Select
            allowClear
            placeholder="Select sub department"
            options={subDepartments}
            value={formData.subDepartment}
            onChange={(v) => updateField("subDepartment", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Reporting To</label>
          <Select
            allowClear
            showSearch
            placeholder="Select reporting manager"
            options={reportTo}
            value={formData.reportingTo}
            onChange={(v) => updateField("reportingTo", v)}
            className="w-full"
            optionFilterProp="label"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Medical Insurance</label>
          <Select
            allowClear
            placeholder="Select option"
            options={yesNoOptions}
            value={formData.medicalInsurance}
            onChange={(v) => updateField("medicalInsurance", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Travel</label>
          <Select
            allowClear
            placeholder="Select option"
            options={yesNoOptions}
            value={formData.travel}
            onChange={(v) => updateField("travel", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Bonus</label>
          <Select
            allowClear
            placeholder="Select option"
            options={yesNoOptions}
            value={formData.bonus}
            onChange={(v) => updateField("bonus", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Leave Policy</label>
          <Select
            mode="multiple"
            allowClear
            placeholder="Select leave policy"
            options={leavePolicies}
            value={formData.leavePolicy}
            onChange={(v) => updateField("leavePolicy", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Attendance</label>
          <Select
            allowClear
            placeholder="Select attendance policy"
            options={[{ label: "attendance_web", value: "attendance_web" }, { label: "attendance_machine", value: "attendance_machine" }]}
            value={formData.attendancePolicy}
            onChange={(v) => updateField("attendancePolicy", v)}
            className="w-full"
          />
        </div>

        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">Shift</label>
          <Select
            allowClear
            placeholder="Select shift"
            options={shifts}
            value={formData.shift}
            onChange={(v) => updateField("shift", v)}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 my-2">
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          loading={loading}
          type="primary"
          className="bg-primary text-white"
        >
          {loading ? "Applying..." : "Apply"}
        </Button>
      </div>
    </div>
  );
};

export default BulkEditEmployee;
